import { AuctionsNearMeQueryParams } from '../types/AuctionsNearMe.types';
import { createBoundingBoxString } from './createBoundingBoxString';
import { fetchCatalogsNearMeByBounds } from '@/redux/modules/auctionsNearMe/auctionsNearMe.actions';
import { GeoArea } from '@/types/GeoArea';
import { MapBounds } from '../components/MapView/components/GoogleMap/AuctionsNearMeGoogleMap';
import { useAppDispatch } from '@/redux/hooks';
import { useEffect } from 'react';

export type UseFetchCatalogsRelatedGeoAreaParams = {
    currentActiveBoundsRef: { current: MapBounds | null };
    currentPage: number;
    customSearchBounds: MapBounds | null;
    geoArea: GeoArea | undefined;
    lastFetchParamsRef: { current: { bounds: string; page: number; pageSize: number } | null };
    pageSize: number;
    queryParams: AuctionsNearMeQueryParams;
    // TODO: Remove feature flag once new auctions near me page is fully deployed
    // @see https://liveauctioneers.atlassian.net/browse/SG-229
    useNewAuctionsNearMePage: boolean;
};

/**
 * Custom hook to fetch catalogs when navigating to a predefined geoArea via RelatedGeoAreas links
 * This handles clicking a RelatedGeoAreas ("More Locations" in the UI) link to a location that
 * is in our predefined geoAreas list (e.g., Miami, FL)
 * e.g., clicking "Miami, FL" RelatedGeoAreas link -> /find-auctions-near-me/fl/miami -> fetch catalogs for Miami, FL
 */
export const useFetchCatalogsRelatedGeoArea = ({
    currentActiveBoundsRef,
    currentPage,
    customSearchBounds,
    geoArea,
    lastFetchParamsRef,
    pageSize,
    queryParams,
    useNewAuctionsNearMePage,
}: UseFetchCatalogsRelatedGeoAreaParams) => {
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!useNewAuctionsNearMePage) {
            return;
        }

        // Only run for predefined geoAreas (not custom searches)
        if (!geoArea || customSearchBounds || queryParams.search) {
            return;
        }

        // Check if geoArea has valid bounds
        if (!geoArea.northEastLatitude || !geoArea.southWestLatitude) {
            return;
        }

        const bounds: MapBounds = {
            northEast: { lat: geoArea.northEastLatitude, lng: geoArea.northEastLongitude },
            southWest: { lat: geoArea.southWestLatitude, lng: geoArea.southWestLongitude },
        };

        const insideBoundingBox = createBoundingBoxString(bounds);

        // Check if we already fetched with these exact params
        const paramsChanged =
            !lastFetchParamsRef.current ||
            lastFetchParamsRef.current.bounds !== insideBoundingBox ||
            lastFetchParamsRef.current.page !== currentPage ||
            lastFetchParamsRef.current.pageSize !== pageSize;

        if (!paramsChanged) {
            return;
        }

        // Store bounds in ref for pagination to use
        currentActiveBoundsRef.current = bounds;

        // Update last fetch params ref
        lastFetchParamsRef.current = { bounds: insideBoundingBox, page: currentPage, pageSize: pageSize };

        dispatch(
            fetchCatalogsNearMeByBounds({
                insideBoundingBox,
                page: currentPage,
                pageSize: pageSize,
            })
        );
    }, [
        useNewAuctionsNearMePage,
        geoArea,
        customSearchBounds,
        queryParams.search,
        currentPage,
        pageSize,
        currentActiveBoundsRef,
        lastFetchParamsRef,
        dispatch,
    ]);
};
